/**
 * Cascading Location Fields Component → Single-Address Entry Group
 *
 * board2-#79 「地址格式改成一個textfield，丟到googlet拆分」
 * (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): forms keep ONLY
 * postcode / country / address (+ notes where the form already has notes).
 * The state/city Selects are REMOVED — the whole street address is one
 * textfield, and the backend parses it via Google Maps into stored
 * stateId/cityId (parse OUTPUTS; mocks simulate this with mockParseAddress).
 *
 * The group this component now renders:
 * - Country Select (behavior unchanged: options/label fallback, clears the
 *   legacy state/city parse-output fields on change via the consumer's
 *   onChangeCallback contract)
 * - Postcode Input (opt-in via postcodeFieldName; keeps the usePostcodeLookup
 *   wiring through onPostcodeChange/onPostcodeBlur)
 * - Single full-address Input (opt-in via addressFieldName; bilingual
 *   placeholder from common.json location.addressPlaceholder)
 *
 * Postcode/address inputs render only when their field names are passed so
 * existing consumers (which still render their own postcode/address rows)
 * neither break nor double-render before their slices land.
 *
 * Deprecated props (state/city era) are still ACCEPTED and ignored/used only
 * for clearing, so every consumer compiles unchanged — see the
 * DEPRECATED(board2-#79) notes on the prop surface.
 *
 * Usage (new single-address form):
 * <CascadingLocationFields
 *   control={control}
 *   errors={errors}
 *   watch={watch}
 *   setValue={setValue}
 *   countryFieldName="countryId"
 *   useCountryId={true}
 *   postcodeFieldName="postcode"
 *   addressFieldName="address"
 *   onPostcodeChange={handlePostcodeChange}
 *   onPostcodeBlur={handlePostcodeBlur}
 * />
 */

import React, { useCallback, useMemo } from 'react';
import { Row, Col, Input } from 'antd';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledSelect } from './ControlledSelect';
import { FormFieldWrapper } from './FormFieldWrapper';
import { useCountriesData } from '@/hooks/useLocations';
import { useWizardStyles } from '@/styles/wizardStyles';
import { AddressParseFailedTag } from '@/components/common/AddressParseFailedTag';
import type { AddressParseStatus } from '@/hooks/mockParseAddress';

export interface CascadingLocationFieldsProps<TFieldValues extends FieldValues = FieldValues> {
  /** React Hook Form control */
  control: Control<TFieldValues>;

  /** Form errors */
  errors: FieldErrors<TFieldValues>;

  /** Watch function from useForm */
  watch: UseFormWatch<TFieldValues>;

  /** SetValue function from useForm */
  setValue: UseFormSetValue<TFieldValues>;

  /** Field name for country (stores countryCode string by default, or countryId number if useCountryId=true) */
  countryFieldName?: Path<TFieldValues>;

  /** Field name for country label (optional, for storing/reading country name for fallback display) */
  countryLabelFieldName?: Path<TFieldValues>;

  /**
   * DEPRECATED(board2-#79): state is a backend-parse OUTPUT, not a form
   * field. Accepted only so legacy consumers compile; used solely to clear
   * a stale parse-output value when the country changes.
   */
  stateFieldName?: Path<TFieldValues>;

  /** DEPRECATED(board2-#79): cleared-on-country-change only; no Select renders. */
  stateLabelFieldName?: Path<TFieldValues>;

  /**
   * DEPRECATED(board2-#79): city is a backend-parse OUTPUT, not a form
   * field. Accepted only so legacy consumers compile; used solely to clear
   * a stale parse-output value when the country changes.
   */
  cityFieldName?: Path<TFieldValues>;

  /** DEPRECATED(board2-#79): cleared-on-country-change only; no Select renders. */
  cityLabelFieldName?: Path<TFieldValues>;

  /** Field name for the postcode input (renders the input when provided) */
  postcodeFieldName?: Path<TFieldValues>;

  /** Field name for the single full-address input (renders the input when provided) */
  addressFieldName?: Path<TFieldValues>;

  /** Whether country field is required */
  countryRequired?: boolean;

  /** DEPRECATED(board2-#79): state Select removed — accepted and ignored. */
  stateRequired?: boolean;

  /** DEPRECATED(board2-#79): city Select removed — accepted and ignored. */
  cityRequired?: boolean;

  /** Whether the postcode field is required */
  postcodeRequired?: boolean;

  /** Whether the address field is required */
  addressRequired?: boolean;

  /** Custom labels */
  labels?: {
    country?: string;
    /** DEPRECATED(board2-#79): state Select removed — ignored. */
    state?: string;
    /** DEPRECATED(board2-#79): city Select removed — ignored. */
    city?: string;
    postcode?: string;
    address?: string;
  };

  /** DEPRECATED(board2-#79): help texts belonged to the removed state/city Selects — ignored. */
  showHelp?: boolean;

  /** Use countryId (number) instead of countryCode (string) for country field value */
  useCountryId?: boolean;

  /** When true, displays fields in read-only mode */
  readOnly?: boolean;

  /** Language override for fetching localized location names (e.g., 'en', 'zh') */
  langOverride?: string | null;

  /**
   * DEPRECATED(board2-#79): the fallback fed the removed state/city Selects —
   * accepted and ignored. Postcode lookup now feeds the mock parse instead.
   */
  postcodeLookupFallback?: {
    stateId: number;
    stateName: string;
    cityId: number;
    cityName: string;
  } | null;

  /** usePostcodeLookup wiring: debounced lookup on postcode typing */
  onPostcodeChange?: (postcode: string) => void;

  /** usePostcodeLookup wiring: immediate lookup on postcode blur */
  onPostcodeBlur?: (postcode: string) => void;

  /**
   * board2-#79/#101 (BE-03): stored parse status of the row being edited.
   * When 'failed' (non-empty input the backend parse could not resolve), a
   * small warning Tag renders under the address input so the error is
   * visible instead of a silent pending+dash. 'pending'/'parsed' render
   * nothing extra.
   */
  parseStatus?: AddressParseStatus | null;
}

/**
 * Single-address entry group: country Select + postcode Input + one
 * full-address Input. State/city are backend-parse outputs and no longer
 * render here (board2-#79).
 */
export function CascadingLocationFields<TFieldValues extends FieldValues = FieldValues>({
  control,
  errors: _errors,
  watch,
  setValue,
  countryFieldName = 'country' as Path<TFieldValues>,
  countryLabelFieldName,
  stateFieldName = 'stateId' as Path<TFieldValues>,
  stateLabelFieldName,
  cityFieldName = 'cityId' as Path<TFieldValues>,
  cityLabelFieldName,
  postcodeFieldName,
  addressFieldName,
  countryRequired = true,
  stateRequired: _stateRequired,
  cityRequired: _cityRequired,
  postcodeRequired = false,
  addressRequired = false,
  labels = {},
  showHelp: _showHelp,
  useCountryId = false,
  readOnly = false,
  langOverride,
  postcodeLookupFallback: _postcodeLookupFallback,
  onPostcodeChange,
  onPostcodeBlur,
  parseStatus,
}: CascadingLocationFieldsProps<TFieldValues>) {
  const { t } = useTranslation('common');
  const styles = useWizardStyles();

  // Watch selected country for the label-fallback option
  const selectedCountryValue = watch(countryFieldName);

  // Watch country label field for fallback display (when editing existing data)
  const countryLabel = countryLabelFieldName ? watch(countryLabelFieldName) : null;

  // Fetch countries
  const { countries, isLoading: isLoadingCountries } = useCountriesData(langOverride);

  // Handle country change — store value/label and clear the legacy
  // state/city parse-output fields (a country change invalidates any
  // previously parsed split; the backend re-parses on save).
  const handleCountryChange = useCallback(
    (value: string | number | string[] | number[]) => {
      if (useCountryId) {
        // Country should be a number in countryId mode
        if (typeof value !== 'number') return;
        setValue(countryFieldName, value as PathValue<TFieldValues, Path<TFieldValues>>);
        // Store country label if field name provided
        if (countryLabelFieldName && countries) {
          const country = countries.find((c) => c.countryId === value);
          if (country) {
            setValue(countryLabelFieldName, country.countryName as PathValue<TFieldValues, Path<TFieldValues>>);
          }
        }
      } else {
        // Country should be a string in countryCode mode
        if (typeof value !== 'string') return;
        setValue(countryFieldName, value as PathValue<TFieldValues, Path<TFieldValues>>);
        // Store country label if field name provided
        if (countryLabelFieldName && countries) {
          const country = countries.find((c) => c.countryCode === value);
          if (country) {
            setValue(countryLabelFieldName, country.countryName as PathValue<TFieldValues, Path<TFieldValues>>);
          }
        }
      }
      // Clear stale parse-output fields on legacy forms (DEPRECATED surface)
      setValue(stateFieldName, null as PathValue<TFieldValues, Path<TFieldValues>>);
      if (stateLabelFieldName) setValue(stateLabelFieldName, '' as PathValue<TFieldValues, Path<TFieldValues>>);
      setValue(cityFieldName, null as PathValue<TFieldValues, Path<TFieldValues>>);
      if (cityLabelFieldName) setValue(cityLabelFieldName, '' as PathValue<TFieldValues, Path<TFieldValues>>);
    },
    [setValue, countryFieldName, countryLabelFieldName, stateFieldName, stateLabelFieldName, cityFieldName, cityLabelFieldName, useCountryId, countries]
  );

  // Prepare options - use countryId or countryCode based on mode
  // Include fallback option from label field if value exists but not in options yet
  const countryOptions = useMemo(() => {
    const optionsFromApi = countries?.map((country) => ({
      label: country.countryName,
      value: useCountryId ? country.countryId : country.countryCode,
    })) || [];

    // Add fallback from label field if we have a selected value but API hasn't loaded yet
    if (selectedCountryValue && countryLabel && typeof countryLabel === 'string') {
      const fallbackExists = optionsFromApi.some((opt) => opt.value === selectedCountryValue);
      if (!fallbackExists) {
        return [
          { label: countryLabel, value: selectedCountryValue as string | number },
          ...optionsFromApi,
        ];
      }
    }

    return optionsFromApi;
  }, [countries, useCountryId, selectedCountryValue, countryLabel]);

  return (
    <Row gutter={[16, 16]}>
      {/* Country */}
      <Col span={8}>
        <ControlledSelect
          name={countryFieldName}
          control={control}
          label={labels.country || t('location.country')}
          required={countryRequired}
          options={countryOptions}
          placeholder={t('location.selectCountry')}
          showSearch
          loading={isLoadingCountries}
          readOnly={readOnly}
          rules={countryRequired ? { required: t('location.countryRequired') } : undefined}
          onChangeCallback={handleCountryChange}
        />
      </Col>

      {/* Postcode — keeps the usePostcodeLookup wiring (debounce on change,
          immediate on blur) so the mock parse has its lookup input */}
      {postcodeFieldName && (
        <Col span={8}>
          <Controller
            name={postcodeFieldName}
            control={control}
            rules={postcodeRequired ? { required: t('location.postcodeRequired') } : undefined}
            render={({ field, fieldState: { error } }) => (
              <FormFieldWrapper
                label={labels.postcode || t('location.postcode')}
                required={postcodeRequired}
                error={error?.message}
              >
                <Input
                  {...field}
                  value={field.value ?? ''}
                  size="large"
                  placeholder={t('location.postcodePlaceholder')}
                  readOnly={readOnly}
                  onChange={(e) => {
                    field.onChange(e);
                    onPostcodeChange?.(e.target.value);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    onPostcodeBlur?.(String(field.value ?? ''));
                  }}
                  style={error ? styles.formInputError : styles.formInput}
                />
              </FormFieldWrapper>
            )}
          />
        </Col>
      )}

      {/* Single full-address textfield — the whole street address in ONE
          field; the backend splits it (Google Maps) into stateId/cityId */}
      {addressFieldName && (
        <Col span={24}>
          <Controller
            name={addressFieldName}
            control={control}
            rules={addressRequired ? { required: t('location.addressRequired') } : undefined}
            render={({ field, fieldState: { error } }) => (
              <FormFieldWrapper
                label={labels.address || t('location.address')}
                required={addressRequired}
                error={error?.message}
              >
                <Input
                  {...field}
                  value={field.value ?? ''}
                  size="large"
                  placeholder={t('location.addressPlaceholder')}
                  readOnly={readOnly}
                  style={error ? styles.formInputError : styles.formInput}
                />
              </FormFieldWrapper>
            )}
          />
        </Col>
      )}

      {/* #101 BE-03: the stored row failed the backend parse → visible
          warning. Rendered independently of addressFieldName so legacy
          consumers that render their own address row can still opt in by
          passing parseStatus. */}
      {parseStatus === 'failed' && (
        <Col span={24}>
          <AddressParseFailedTag style={{ marginInlineStart: 0 }} />
        </Col>
      )}
    </Row>
  );
}

export default CascadingLocationFields;
